"use client"

import {
  LineChart as RLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Brush, ReferenceLine, ReferenceDot, ReferenceArea, ErrorBar, LabelList,
} from "recharts"
import {
  type BaseChartProps, DEFAULT_PALETTE, inferSeries, colorFor,
} from "./types"
import type { ReactElement } from "react"

export interface LineSeriesConfig {
  /** Series name (matches the data field). */
  name: string
  /** Line stroke color override. */
  color?: string
  /** Stroke dash pattern, e.g. "4 4". */
  dashArray?: string
  /** Render straight (linear) instead of monotone-curved. */
  straight?: boolean
  /** Render a stepped/staircase line. */
  stepped?: boolean
  /** Show point dots; pass `false` to hide. */
  showDots?: boolean
  /** Field name in each row that holds an error-bar magnitude. */
  errorKey?: string
  /** Show value labels at each point. */
  showLabels?: boolean
}

export interface LineChartProps extends BaseChartProps {
  /** Render straight (linear) instead of monotone-curved lines. */
  straight?: boolean
  /** Render a stepped/staircase line. */
  stepped?: boolean
  /** Two Y axes — first half of series on left, rest on right. */
  biaxial?: boolean
  /** Show point dots on the line. */
  showDots?: boolean
  /** Skip nulls and connect across the gap. */
  connectNulls?: boolean
  /** Add a brush (range slider) at the bottom. */
  withBrush?: boolean
  /** Reference horizontal lines (e.g. target threshold). */
  referenceLines?: { y: number; label?: string; color?: string }[]
  /** Reference vertical band(s) (e.g. forecast region). */
  referenceAreas?: { x1: string | number; x2: string | number; label?: string; color?: string }[]
  /** Single point markers (e.g. annotated peaks). */
  referenceDots?: { x: string | number; y: number; label?: string; color?: string }[]
  /** Render the chart vertically (Y-axis = category). */
  vertical?: boolean
  /** Optional pixel padding either side of X axis (recharts xAxisPadding). */
  xPadding?: { left?: number; right?: number }
  /** Sync this chart's tooltip with another chart that shares the same id. */
  syncId?: string
  /** Per-series customisation. Falls back to global props when not set. */
  seriesConfig?: Record<string, LineSeriesConfig>
}

export function LineChart({
  data, series, xKey = "name", palette = DEFAULT_PALETTE, colorMap,
  showLegend = true, showGrid = true, showTooltip = true,
  xLabel, yLabel, compact, className, style, height = "100%", width = "100%",
  straight, stepped, biaxial, showDots = true, connectNulls, withBrush,
  referenceLines, referenceAreas, referenceDots, vertical, xPadding, syncId, seriesConfig,
}: LineChartProps) {
  const seriesList = series ?? inferSeries(data, xKey)
  const defaultType: "linear" | "monotone" | "step" = stepped ? "step" : straight ? "linear" : "monotone"

  const numberAxis = (yAxisId: string) => (
    <YAxis key={yAxisId} yAxisId={yAxisId} type="number" tick={{ fontSize: 10.5 }}
      orientation={yAxisId === "right" ? "right" : "left"}
      label={yAxisId === "left" && yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />
  )

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RLineChart
        data={data}
        layout={vertical ? "vertical" : "horizontal"}
        syncId={syncId}
        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
      >
        {showGrid && !compact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        {!compact && (vertical ? (
          <XAxis type="number" tick={{ fontSize: 10.5 }} padding={xPadding} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} />
        ) : (
          <XAxis dataKey={xKey} tick={{ fontSize: 10.5 }} padding={xPadding} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />
        ))}
        {!compact && (vertical ? (
          <YAxis dataKey={xKey} type="category" tick={{ fontSize: 10.5 }} width={80} />
        ) : numberAxis("left"))}
        {biaxial && !vertical && !compact && numberAxis("right")}
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {referenceAreas?.map((r, i) => (
          <ReferenceArea key={`ra-${i}`} x1={r.x1} x2={r.x2} stroke={r.color || "#888"} fill={r.color || "#888"} fillOpacity={0.08} label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {referenceLines?.map((r, i) => (
          <ReferenceLine key={`rl-${i}`} yAxisId="left" y={r.y} stroke={r.color || "#888"} strokeDasharray="4 4" label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {referenceDots?.map((r, i) => (
          <ReferenceDot key={`rd-${i}`} yAxisId="left" x={r.x} y={r.y} r={4} fill={r.color || "#ef4444"} stroke="white" label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {seriesList.map((name, i): ReactElement => {
          const cfg: Partial<LineSeriesConfig> = seriesConfig?.[name] || {}
          const c = cfg.color || colorFor(name, i, palette, colorMap)
          const lineType = cfg.stepped ? "step" : cfg.straight ? "linear" : defaultType
          return (
            <Line
              key={name}
              type={lineType}
              dataKey={name}
              yAxisId={biaxial ? (i < seriesList.length / 2 ? "left" : "right") : "left"}
              stroke={c}
              strokeDasharray={cfg.dashArray}
              strokeWidth={2.5}
              dot={cfg.showDots ?? showDots ? { r: 3 } : false}
              activeDot={{ r: 5 }}
              connectNulls={connectNulls}
              isAnimationActive={false}
            >
              {cfg.errorKey && <ErrorBar dataKey={cfg.errorKey} stroke={c} strokeWidth={1.5} />}
              {cfg.showLabels && <LabelList dataKey={name} position="top" style={{ fontSize: 10 }} />}
            </Line>
          )
        })}
        {withBrush && <Brush dataKey={xKey} height={20} stroke={palette[0]} />}
      </RLineChart>
    </ResponsiveContainer>
  )
}
